import React, { useState } from "react";
import { Tooltip as AntTooltip } from "antd";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import type { PageMetric, Period } from "@/services/searchPerformanceService";
import { PageDetailPanel } from "@/components/dashboard/tables/PageDetailPanel";

export interface SearchPagesTableProps {
	pages: PageMetric[];
	sortBy: string;
	sortDir: "asc" | "desc";
	onSort: (col: string) => void;
	period: Period;
	tenantId: string | null;
	useSampleData?: boolean;
	/** Detail route relabels this column "Avg position (unweighted)". */
	positionHeader?: string;
}

export function SearchPagesTable({
	pages,
	sortBy,
	sortDir,
	onSort,
	period,
	tenantId,
	useSampleData = false,
	positionHeader = "Position",
}: SearchPagesTableProps) {
	const [expandedRow, setExpandedRow] = useState<string | null>(null);

	const SortIcon = ({ col }: { col: string }) =>
		sortBy === col ? (
			sortDir === "desc" ? <ChevronDown className="inline h-3 w-3" /> : <ChevronUp className="inline h-3 w-3" />
		) : null;

	return (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b bg-muted text-left text-xs font-medium text-muted-foreground">
						<th className="py-3 pl-5 pr-3">Page</th>
						<th
							className="cursor-pointer py-3 pr-3 text-right hover:text-foreground"
							onClick={() => onSort("impressions")}
							onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort("impressions"); } }}
							tabIndex={0}
							aria-sort={sortBy === "impressions" ? (sortDir === "desc" ? "descending" : "ascending") : "none"}
						>
							<AntTooltip title="Times this page appeared in Google results"><span>Impressions</span></AntTooltip> <SortIcon col="impressions" />
						</th>
						<th
							className="cursor-pointer py-3 pr-3 text-right hover:text-foreground"
							onClick={() => onSort("clicks")}
							onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort("clicks"); } }}
							tabIndex={0}
							aria-sort={sortBy === "clicks" ? (sortDir === "desc" ? "descending" : "ascending") : "none"}
						>
							<AntTooltip title="Times someone clicked through to this page from Google"><span>Clicks</span></AntTooltip> <SortIcon col="clicks" />
						</th>
						<th
							className="cursor-pointer py-3 pr-3 text-right hover:text-foreground"
							onClick={() => onSort("ctr")}
							onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort("ctr"); } }}
							tabIndex={0}
							aria-sort={sortBy === "ctr" ? (sortDir === "desc" ? "descending" : "ascending") : "none"}
						>
							<AntTooltip title="Click-through rate — clicks divided by impressions"><span>CTR</span></AntTooltip> <SortIcon col="ctr" />
						</th>
						<th
							className="cursor-pointer py-3 pr-5 text-right hover:text-foreground"
							onClick={() => onSort("position")}
							onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort("position"); } }}
							tabIndex={0}
							aria-sort={sortBy === "position" ? (sortDir === "desc" ? "descending" : "ascending") : "none"}
						>
							<AntTooltip title="Average Google ranking position (lower = better, 1-10 = page 1)"><span>{positionHeader}</span></AntTooltip> <SortIcon col="position" />
						</th>
					</tr>
				</thead>
				<tbody>
					{pages.map((page) => {
						const isExpanded = expandedRow === page.page_url;
						const toggle = () => setExpandedRow(isExpanded ? null : page.page_url);
						return (
							<React.Fragment key={page.page_url}>
								<tr className="cursor-pointer border-b border-border transition-colors hover:bg-muted" onClick={toggle}>
									<td className="max-w-[400px] py-3 pl-5 pr-3">
										<div className="flex items-start gap-2">
											<span
												role="button"
												tabIndex={0}
												aria-label={isExpanded ? "Collapse row details" : "Expand row details"}
												aria-expanded={isExpanded}
												onClick={(e) => {
													e.stopPropagation();
													toggle();
												}}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														e.stopPropagation();
														toggle();
													}
												}}
												className="mt-0.5 inline-flex shrink-0 cursor-pointer rounded text-muted-foreground hover:text-foreground"
											>
												{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
											</span>
											<div className="min-w-0">
												<p className="truncate font-medium">{page.blog_title || page.page_url.replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/"}</p>
												{page.keyword && <p className="truncate text-xs text-muted-foreground">{page.keyword}</p>}
											</div>
										</div>
									</td>
									<td className="py-3 pr-3 text-right font-medium">{page.impressions.toLocaleString()}</td>
									<td className="py-3 pr-3 text-right font-medium text-coral">{page.clicks.toLocaleString()}</td>
									<td className="py-3 pr-3 text-right">{(page.ctr * 100).toFixed(1)}%</td>
									<td className="py-3 pr-5 text-right">{page.position.toFixed(1)}</td>
								</tr>
								{isExpanded && (
									<tr key={`${page.page_url}-detail`}>
										<td colSpan={5} className="border-b border-border bg-muted/40">
											<PageDetailPanel pageUrl={page.page_url} period={period} tenantId={tenantId} useSampleData={useSampleData} />
										</td>
									</tr>
								)}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
